import { AccordionSectionPresentationProps } from "@DSG/MobiusOverrides/AccordionSection/AccordionSection";
import ManagedAccordionSection from "@DSG/MobiusOverrides/AccordionSection/ManagedAccordionSection";
import colorPallete, {
    bodyFonts,
    headlineFonts,
} from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import { ListItem, UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    showTopContent = "showTopContent",
    topContent = "topContent",
    stateItems = "stateItems",
    actionButtonText = "actionButtonText",
    showBottomContent = "showBottomContent",
    bottomContent = "bottomContent",
}

interface DSGStateItems {
    fields: {
        stateName: string;
        hideState: boolean;
        branchLocations: BranchLocationsItems[];
    };
}

interface BranchLocationsItems {
    fields: {
        branchName: string;
        branchPhoneNumber: string;
        openActionButtonLinkInNewWindow: string;
        actionButtonLinkUrl: string;
        hideBranch: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showTopContent]: boolean;
        [fields.topContent]: string;
        [fields.stateItems]: DSGStateItems[];
        [fields.actionButtonText]: string;
        [fields.showBottomContent]: boolean;
        [fields.bottomContent]: string;
    };
}

export interface DsgBranchStateAndLocationDetailsWidgetStyles {
    wrapper?: InjectableCss;
    topContentWrapper?: InjectableCss;
    bottomContentWrapper?: InjectableCss;
    accordionSection?: AccordionSectionPresentationProps;
    branchStatesWrapper?: InjectableCss;
    branchLocationListWrapper?: InjectableCss
    branchLocationListItem?: InjectableCss;
    branchLocationName?: TypographyProps;
    phoneNumberLink?: TypographyProps;
    actionButtonLink?: TypographyProps;
}

export const dsgBranchStateAndLocationDetailsWidgetStyles: DsgBranchStateAndLocationDetailsWidgetStyles = {
    wrapper: {
        css: css`
            padding: 24px;
            max-width: 1120px;
            margin: auto;  
            display: flex;
            flex-direction: column;
            gap: 24px;
        `
    },
    branchStatesWrapper: {
        css: css`
            max-width: 976px;
            margin: auto;
        `
    },
    accordionSection: {
        headerProps: {
            css: css`
                button {
                    background: ${colorPallete.white};
                    border: none;
                    border-bottom: 1px solid ${colorPallete.grey500};
                    padding: 24px;
                    &[aria-expanded="true"] {
                        border: none;
                        padding-bottom: 0;
                    }
                    span {
                        color: ${colorPallete.grey800};
                        font: ${headlineFonts.headline3SemiBoldLg.fontWeight} ${headlineFonts.headline3SemiBoldLg.fontSize}/${headlineFonts.headline3SemiBoldLg.lineHeight} ${headlineFonts.headline3SemiBoldLg.fontFamily};
                    }
                    [class*="TransitionManager"] {
                        padding: 0;
                        bottom: 0;
                        display: flex;
                    }
                }   
            `
        },
        panelProps: {
            css: css`
                background: ${colorPallete.white};
                border: none;  
                padding: 24px;
                border-bottom: 1px solid ${colorPallete.grey500};
            `
        },
    },
    branchLocationListWrapper: {
        css: css`
            list-style: none;
            margin: 0 !important;
            display: flex;
            flex-wrap: wrap;
            padding: 0;
            gap: 24px;
        `
    },
    branchLocationListItem: {
        css: css`
            max-width: calc(50% - 12px);
            flex-basis: calc(50% - 12px);
            display: flex;
            flex-direction: column;
            gap: 4px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        max-width: calc(100%/3 - 40px);
                        flex-basis: calc(100%/3 - 40px);
                    `,
                    css`
                        max-width: 262px;
                        flex-basis: 262px;
                    `,
                ],
                "min",
            )}
        `
    },
    branchLocationName: {
        css: css`
            font: ${bodyFonts.body1SemiBoldLg.fontWeight} ${bodyFonts.body1SemiBoldLg.fontSize}/${bodyFonts.body1SemiBoldLg.lineHeight} ${bodyFonts.body1SemiBoldLg.fontFamily};
        ` 
    },
    phoneNumberLink: {
        css: css`
            font:${bodyFonts.body2RegularLg.fontWeight} ${bodyFonts.body2RegularLg.fontSize}/${bodyFonts.body2RegularLg.lineHeight} ${bodyFonts.body2RegularLg.fontFamily};
        `   
    },
    actionButtonLink: {
        css: css`
            font:${bodyFonts.body2RegularLg.fontWeight} ${bodyFonts.body2RegularLg.fontSize}/${bodyFonts.body2RegularLg.lineHeight} ${bodyFonts.body2RegularLg.fontFamily};
        ` 
    }
};

const styles = dsgBranchStateAndLocationDetailsWidgetStyles;

export const DsgBranchStateAndLocationDetailsWidget: FC<OwnProps> = ({ fields }) => {
    const { stateItems } = fields;

    return (
        <StyledWrapper {...styles.wrapper} className="branchLocationComponent">
            {(fields.showTopContent && fields.topContent) && (
                <StyledWrapper {...styles.topContentWrapper}>
                    {parse(fields.topContent, parserOptions)}
                </StyledWrapper>
            )}
            
            {stateItems.length > 0 && (
                <Accordion headingLevel={4}>
                    {stateItems.map((item, index) => (
                        <>
                            {item.fields.stateName && !item.fields.hideState && (
                                <StyledWrapper key={`branchStates${index}`} {...styles.branchStatesWrapper}>
                                    <ManagedAccordionSection
                                        initialExpanded={false}
                                        title={item.fields.stateName}
                                        {...styles.accordionSection}
                                    >
                                        <UnorderedList {...styles.branchLocationListWrapper}>
                                            {item.fields.branchLocations &&
                                                item.fields.branchLocations.map((location, locationIndex) => (
                                                <>
                                                    {!location.fields.hideBranch && (
                                                        <ListItem key={`branchLocation${index}${locationIndex}`} {...styles.branchLocationListItem}>
                                                            {location.fields.branchName && (
                                                                <Typography {...styles.branchLocationName}>
                                                                    {location.fields.branchName}
                                                                </Typography>
                                                            )}
                                                            {location.fields.branchPhoneNumber && (
                                                                <Link  href={`tel:${location.fields.branchPhoneNumber}`}
                                                                    className="text-link-secondary text-link-active" 
                                                                    {...styles.phoneNumberLink}>
                                                                    {location.fields.branchPhoneNumber}
                                                                </Link>
                                                            )}
                                                            {(location.fields.actionButtonLinkUrl && fields.actionButtonText) && (
                                                                <Link href={location.fields.actionButtonLinkUrl}
                                                                    className="text-link-primary" 
                                                                    target={location.fields.openActionButtonLinkInNewWindow ? '_blank' : ''}
                                                                    {...styles.actionButtonLink}>
                                                                    {fields.actionButtonText}
                                                                </Link>
                                                            )}
                                                        </ListItem>
                                                    )}
                                                </>
                                            ))}
                                        </UnorderedList>
                                    </ManagedAccordionSection>
                                </StyledWrapper>
                            )}
                        </>
                    ))}
                </Accordion>
            )}

            {(fields.showBottomContent && fields.bottomContent) && (
                <StyledWrapper {...styles.bottomContentWrapper}>
                    {parse(fields.bottomContent, parserOptions)}
                </StyledWrapper>
            )}
          </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: DsgBranchStateAndLocationDetailsWidget,
    definition: {
        group: "Common",
        displayName: "Branch Component",
        fieldDefinitions: [
            {
                name: fields.showTopContent,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.topContent,
                displayName: "Top Content",
                editorTemplate: "RichTextField",
                defaultValue: `
                    <div style="text-align:center;">
                        <h2 style="color:#fff;margin:0 0 8px;">Ready to learn more or get started?</h2>
                        <h4 style="color:#fff;margin:0 0 8px;">Find and connect with your local DSG experts below.</h4>
                        <p style="color:#fff;margin:0;">We currently are offering these products in DSG locations except for Iowa and Michigan.</p>
                    </div>
                `,
                fieldType: "Contextual",
                extendedConfig: { height: 200 },
                sortOrder: 1,
                isVisible: widget => widget.fields.showTopContent,
            },
            {
                name: fields.stateItems,
                editorTemplate: "ListField",
                displayName: "Branch State Details",
                getDisplay: (item: HasFields, state) => {
                    const {
                        stateName
                    } = item.fields;
                    if (stateName) {
                        return stateName;
                    }
                },
                defaultValue: [
                    {
                        fields: {
                            stateName: "Iowa",
                            hideState: false,
                            branchLocations: [
                                {
                                    fields: {
                                        branchName: "Ames",
                                        branchPhoneNumber: "(515) 233-2245",
                                        actionButtonLinkUrl: '/location?ames-ia',
                                        openActionButtonLinkInNewWindow: false,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Cedar Rapids",
                                        branchPhoneNumber: "(319) 826-1008",
                                        actionButtonLinkUrl: '/location?cedar%20rapids-ia',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Fort Dodge",
                                        branchPhoneNumber: "(515) 576-7239",
                                        actionButtonLinkUrl: '/location?fort%20dodge-ia',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Sioux City",
                                        branchPhoneNumber: "(712) 454-1867",
                                        actionButtonLinkUrl: '/location?sioux%20city-ia',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Spirit Lake",
                                        branchPhoneNumber: "(712) 339-4480",
                                        actionButtonLinkUrl: '/location?spirit%20lake-ia',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                }
                            ]
                        }
                    },
                    {
                        fields: {
                            stateName: "Michigan",
                            hideState: false,
                            branchLocations: [
                                {
                                    fields: {
                                        branchName: "Almont",
                                        branchPhoneNumber: "(810) 798-0551",
                                        actionButtonLinkUrl: '/location?almont-mi',
                                        openActionButtonLinkInNewWindow: false,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Grand Rapids, MI",
                                        branchPhoneNumber: "(616) 791-8580",
                                        actionButtonLinkUrl: '/location?grand%20rapids,%20mi-mi',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Lansing",
                                        branchPhoneNumber: "(517) 887-2424",
                                        actionButtonLinkUrl: '/location?lansing-mi',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Livonia",
                                        branchPhoneNumber: "(734) 462-1108",
                                        actionButtonLinkUrl: '/location?livonia-mi',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Saginaw",
                                        branchPhoneNumber: "(989) 272-9272",
                                        actionButtonLinkUrl: '/location?saginaw-mi',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Sterling Heights",
                                        branchPhoneNumber: "(586) 979-1370",
                                        actionButtonLinkUrl: '/location?sterling%20heights-mi',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Waterford",
                                        branchPhoneNumber: "(248) 674-7132",
                                        actionButtonLinkUrl: '/location?waterford-mi',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                }
                            ]
                        }
                    },
                    {
                        fields: {
                            stateName: "Minnesota",
                            hideState: false,
                            branchLocations: [
                                {
                                    fields: {
                                        branchName: "Alexandria",
                                        branchPhoneNumber: "(320) 763-3172",
                                        actionButtonLinkUrl: '/location?alexandria-mn',
                                        openActionButtonLinkInNewWindow: false,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Austin",
                                        branchPhoneNumber: "(507) 433-4770",
                                        actionButtonLinkUrl: '/location?austin-mn',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Bemidji",
                                        branchPhoneNumber: "(218) 444-3272",
                                        actionButtonLinkUrl: '/location?bemidji-mn',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Burnsville",
                                        branchPhoneNumber: "(612) 977-3800",
                                        actionButtonLinkUrl: '/location?burnsville-mn',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Grand Rapids, MN",
                                        branchPhoneNumber: "(218) 327-3322",
                                        actionButtonLinkUrl: '/location?grand rapids, mn-mn',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Ham Lake",
                                        branchPhoneNumber: "(763) 784-4478",
                                        actionButtonLinkUrl: '/location?ham lake-mn',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Mankato",
                                        branchPhoneNumber: "(507) 304-6630",
                                        actionButtonLinkUrl: '/location?mankato-mn',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Oakdale",
                                        branchPhoneNumber: "(651) 777-1044",
                                        actionButtonLinkUrl: '/location?oakdale-mn',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Otsego",
                                        branchPhoneNumber: "(763) 295-3588",
                                        actionButtonLinkUrl: '/location?otsego-mn',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Plymouth",
                                        branchPhoneNumber: "(952) 935-0445",
                                        actionButtonLinkUrl: '/location?plymouth-mn',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Rochester",
                                        branchPhoneNumber: "(507) 285-2500",
                                        actionButtonLinkUrl: '/location?rochester-mn',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Rogers",
                                        branchPhoneNumber: "(763) 270-2050",
                                        actionButtonLinkUrl: '/location?rogers-mn',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Saint Paul",
                                        branchPhoneNumber: "(651) 224-5781",
                                        actionButtonLinkUrl: '/location?saint paul-mn',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Winona",
                                        branchPhoneNumber: "(507) 454-6407",
                                        actionButtonLinkUrl: '/location?winona-mn',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                            ]
                        }
                    },
                    {
                        fields: {
                            stateName: "Montana",
                            hideState: false,
                            branchLocations: [
                                {
                                    fields: {
                                        branchName: "Billings - Plumbing & HVAC",
                                        branchPhoneNumber: "(406) 656-2082",
                                        actionButtonLinkUrl: '/location?billings - plumbing and hvac-mt',
                                        openActionButtonLinkInNewWindow: false,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Billings - Waterworks",
                                        branchPhoneNumber: "(406) 294-4455",
                                        actionButtonLinkUrl: '/location?billings - waterworks-mt',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Bozeman",
                                        branchPhoneNumber: "(406) 586-8650",
                                        actionButtonLinkUrl: '/location?bozeman-mt',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Butte",
                                        branchPhoneNumber: "(406) 792-2595",
                                        actionButtonLinkUrl: '/location?butte-mt',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Great Falls",
                                        branchPhoneNumber: "(406) 770-5300",
                                        actionButtonLinkUrl: '/location?great falls-mt',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Helena",
                                        branchPhoneNumber: "(406) 443-4012",
                                        actionButtonLinkUrl: '/location?helena-mt',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Kalispell",
                                        branchPhoneNumber: "(406) 752-1402",
                                        actionButtonLinkUrl: '/location?kalispell-mt',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Missoula",
                                        branchPhoneNumber: "(406) 728-0009",
                                        actionButtonLinkUrl: '/location?missoula-mt',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                }
                            ]
                        }
                    },
                    {
                        fields: {
                            stateName: "Nebraska",
                            hideState: false,
                            branchLocations: [
                                {
                                    fields: {
                                        branchName: "South Sioux City",
                                        branchPhoneNumber: "(402) 508-8850",
                                        actionButtonLinkUrl: '/location?south sioux city-ne',
                                        openActionButtonLinkInNewWindow: false,
                                        hideBranch: false,
                                    }
                                },
                            ]
                        }
                    },
                    {
                        fields: {
                            stateName: "North Dakota",
                            hideState: false,
                            branchLocations: [
                                {
                                    fields: {
                                        branchName: "Bismarck - Electrical",
                                        branchPhoneNumber: "(701) 255-7112",
                                        actionButtonLinkUrl: '/location?bismarck - electrical-nd',
                                        openActionButtonLinkInNewWindow: false,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Bismarck - Plumbing & HVAC",
                                        branchPhoneNumber: "(701) 223-3130",
                                        actionButtonLinkUrl: '/location?bismarck - plumbing and hvac-nd',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Bismarck - Waterworks",
                                        branchPhoneNumber: "(701) 221-9070",
                                        actionButtonLinkUrl: '/location?bismarck - waterworks-nd',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Dickinson",
                                        branchPhoneNumber: "(701) 483-6525",
                                        actionButtonLinkUrl: '/location?dickinson-nd',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Fargo",
                                        branchPhoneNumber: "(701) 237-9440",
                                        actionButtonLinkUrl: '/location?fargo-nd',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Fargo - Waterworks",
                                        branchPhoneNumber: "(701) 237-0222",
                                        actionButtonLinkUrl: '/location?fargo - waterworks-nd',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Grand Forks",
                                        branchPhoneNumber: "(701) 746-7343",
                                        actionButtonLinkUrl: '/location?grand forks-nd',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Minot - Electrical",
                                        branchPhoneNumber: "(701) 852-4466",
                                        actionButtonLinkUrl: '/location?minot - electrical-nd',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Minot - Plumbing & HVAC",
                                        branchPhoneNumber: "(701) 852-4035",
                                        actionButtonLinkUrl: '/location?minot - plumbing and hvac-nd',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Williston",
                                        branchPhoneNumber: "(701) 572-2177",
                                        actionButtonLinkUrl: '/location?williston-nd',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                }
                            ]
                        }
                    },
                    {
                        fields: {
                            stateName: "South Dakota",
                            hideState: false,
                            branchLocations: [
                                {
                                    fields: {
                                        branchName: "Aberdeen",
                                        branchPhoneNumber: "(605) 225-7100",
                                        actionButtonLinkUrl: '/location?aberdeen-sd',
                                        openActionButtonLinkInNewWindow: false,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Mitchell",
                                        branchPhoneNumber: "(605) 996-7591",
                                        actionButtonLinkUrl: '/location?mitchell-sd',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Pierre",
                                        branchPhoneNumber: "(605) 224-8855",
                                        actionButtonLinkUrl: '/location?pierre-sd',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Rapid City",
                                        branchPhoneNumber: "(605) 348-7100",
                                        actionButtonLinkUrl: '/location?rapid city-sd',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Rapid City - Waterworks",
                                        branchPhoneNumber: "(605) 513-5170",
                                        actionButtonLinkUrl: '/location?rapid city - waterworks-sd',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Sioux Falls",
                                        branchPhoneNumber: "(605) 336-3090",
                                        actionButtonLinkUrl: '/location?sioux falls-sd',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Sioux Falls - Waterworks",
                                        branchPhoneNumber: "(605) 336-8884",
                                        actionButtonLinkUrl: '/location?sioux falls - waterworks-sd',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Watertown",
                                        branchPhoneNumber: "(605) 657-7800",
                                        actionButtonLinkUrl: '/location?watertown-sd',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                }
                            ]
                        }
                    },
                    {
                        fields: {
                            stateName: "Wisconsin",
                            hideState: false,
                            branchLocations: [
                                {
                                    fields: {
                                        branchName: "Eau Claire",
                                        branchPhoneNumber: "(715) 727-4570",
                                        actionButtonLinkUrl: '/location?eau claire-wi',
                                        openActionButtonLinkInNewWindow: false,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "LaCrosse",
                                        branchPhoneNumber: "(608) 781-2000",
                                        actionButtonLinkUrl: '/location?la crosse-wi',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Plover",
                                        branchPhoneNumber: "(715) 341-5560",
                                        actionButtonLinkUrl: '/location?plover-wi',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Rice Lake",
                                        branchPhoneNumber: "(715) 234-4370",
                                        actionButtonLinkUrl: '/location?rice lake-wi',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                },
                                {
                                    fields: {
                                        branchName: "Sheboygan",
                                        branchPhoneNumber: "(920) 457-7765",
                                        actionButtonLinkUrl: '/location?sheboygan-wi',
                                        openActionButtonLinkInNewWindow: true,
                                        hideBranch: false,
                                    }
                                }
                            ]
                        }
                    }
                ],
                fieldType: "General",
                sortOrder: 2,
                fieldDefinitions: [
                    {
                        name: "stateName",
                        editorTemplate: "TextField",
                        displayName: "State Name",
                        defaultValue: "",
                        sortOrder: 0,
                        isRequired: true,
                    },
                    {
                        name: "hideState",
                        displayName: "Hide State",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        sortOrder: 1,
                    },
                    {
                        name: "branchLocations",
                        editorTemplate: "ListField",
                        displayName: "Branch Locations Details",
                        defaultValue: [],
                        getDisplay: (item: HasFields, state) => {
                            const { branchName } = item.fields;
                            if (branchName) {
                                return branchName;
                            }
                        },
                        fieldDefinitions: [
                            {
                                name: "branchName",
                                editorTemplate: "TextField",
                                displayName: "Branch Name",
                                defaultValue: "",
                                sortOrder: 0,
                                isRequired: true,
                            },
                            {
                                name: "branchPhoneNumber",
                                editorTemplate: "TextField",
                                displayName: "Branch Phone Number",
                                defaultValue: "",
                                sortOrder: 1
                            },
                            {
                                name: "actionButtonLinkUrl",
                                displayName: "Action Button Link Url", 
                                editorTemplate: "TextField",
                                defaultValue: "",
                                sortOrder: 2,
                            },
                            {
                                name: "openActionButtonLinkInNewWindow",
                                displayName: "Open Action Button Link In New Window",
                                editorTemplate: "CheckboxField",
                                defaultValue: false,
                                sortOrder: 3,
                            },
                            {
                                name: "hideBranch",
                                displayName: "Hide Branch",
                                editorTemplate: "CheckboxField",
                                defaultValue: false,
                                sortOrder: 4,
                            },
                        ],
                        sortOrder: 2
                    },
                ],
            },
            {
                name: fields.actionButtonText,
                editorTemplate: "TextField",
                displayName: "Action Button Text",
                defaultValue: "View details",
                fieldType: "Contextual",
                sortOrder: 3,
            },
            {
                name: fields.showBottomContent,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.bottomContent,
                displayName: "Bottom Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                extendedConfig: { height: 200 },
                sortOrder: 5,
                isVisible: widget => widget.fields.showBottomContent,
            },
        ],
    },
};

export default widgetModule;